// Korg Triton/M3 KSF/KMP Exporter
// Creates KSF (Korg Sample File) and KMP (Korg Multi Program) files
// compatible with the Korg Triton, M3, Karma, Kronos, and TR series.
//
// KSF format (chunk-based, Big Endian):
//   SMP1 chunk (32 bytes): sample name, bank, start/loop addresses
//   SNO1 chunk (4 bytes): sample number
//   SMD1 chunk (12 + data): sample rate, attributes, PCM data (16-bit BE)
//
// KMP format (multisample referencing KSF files):
//   MSP1 chunk (18 bytes): name, numSamples, attributes
//   MNO1 chunk (4 bytes): multisample number
//   RLP1 chunk (18 bytes per entry): key range, tune, level, pan, KSF filename
//   RLP2 chunk (4 bytes): transpose, resonance, attack, decay
//
// Triton/M3 samples are 16-bit big-endian PCM, typically 44100 Hz mono.

// ---- Helper: write uint32 big-endian ----
function writeU32BE(buf, off, val) {
   buf[off]     = (val >>> 24) & 0xFF;
   buf[off + 1] = (val >>> 16) & 0xFF;
   buf[off + 2] = (val >>> 8) & 0xFF;
   buf[off + 3] = val & 0xFF;
}

// ---- Helper: write ASCII string into buffer ----
function writeAscii(buf, off, str, len) {
   const bytes = new TextEncoder().encode(str);
   for (let i = 0; i < len; i++) {
      buf[off + i] = i < bytes.length ? bytes[i] : 0x20; // space pad
   }
}

// ---- Helper: write 4-char chunk ID ----
function writeChunkId(buf, off, id) {
   for (let i = 0; i < 4; i++) {
      buf[off + i] = id.charCodeAt(i);
   }
}

// ---- Helper: convert Int16Array to 16-bit big-endian bytes ----
function pcmToBE16(pcm) {
   const out = new Uint8Array(pcm.length * 2);
   for (let i = 0; i < pcm.length; i++) {
      out[i * 2]     = (pcm[i] >> 8) & 0xFF;
      out[i * 2 + 1] = pcm[i] & 0xFF;
   }
   return out;
}

/**
 * Build a KSF (Korg Sample File) from PCM data.
 *
 * @param {Object} sample - { name, pcm: Int16Array, sampleRate, originalKey, loopStart, loopEnd, sampleNumber }
 * @returns {Uint8Array} - KSF file bytes
 */
export function buildKSF(sample) {
   const name = (sample.name || 'SAMPLE').substring(0, 16);
   const pcm = sample.pcm;
   const sampleRate = sample.sampleRate || 44100;
   const origKey = sample.originalKey !== undefined ? sample.originalKey : 60;
   const loopStart = sample.loopStart || 0;
   const loopEnd = sample.loopEnd || pcm.length;
   const sampleNumber = sample.sampleNumber !== undefined ? sample.sampleNumber : 0;

   // Convert PCM to big-endian 16-bit
   const pcmBytes = pcmToBE16(pcm);

   // Chunk sizes
   const SMP1_SIZE = 32;
   const SNO1_SIZE = 4;
   const SMD1_HEADER = 12;
   const SMD1_SIZE = SMD1_HEADER + pcmBytes.length;

   // Total file size: 4+4 per chunk header, chunks are padded to even
   const smp1Total = 8 + SMP1_SIZE;
   const sno1Total = 8 + SNO1_SIZE;
   const smd1Total = 8 + SMD1_SIZE + (SMD1_SIZE & 1); // pad to even

   const totalSize = smp1Total + sno1Total + smd1Total;
   const buf = new Uint8Array(totalSize);
   let pos = 0;

   // SMP1 chunk: sample parameters
   writeChunkId(buf, pos, 'SMP1');
   writeU32BE(buf, pos + 4, SMP1_SIZE);
   pos += 8;
   writeAscii(buf, pos, name, 16);          // sample name (16 bytes)
   buf[pos + 16] = 0;                        // default bank
   // Start address (3 bytes BE) — always 0 for a standalone KSF
   buf[pos + 17] = 0;
   buf[pos + 18] = 0;
   buf[pos + 19] = 0;
   writeU32BE(buf, pos + 20, 0);             // second start address
   writeU32BE(buf, pos + 24, loopStart);     // loop start (in samples)
   writeU32BE(buf, pos + 28, loopEnd);       // loop end (in samples)
   pos += SMP1_SIZE;

   // SNO1 chunk: sample number
   writeChunkId(buf, pos, 'SNO1');
   writeU32BE(buf, pos + 4, SNO1_SIZE);
   pos += 8;
   writeU32BE(buf, pos, sampleNumber);
   pos += SNO1_SIZE;

   // SMD1 chunk: sample data
   writeChunkId(buf, pos, 'SMD1');
   writeU32BE(buf, pos + 4, SMD1_SIZE);
   pos += 8;
   writeU32BE(buf, pos, sampleRate);         // sample rate (uint32 BE)
   buf[pos + 4] = 0;                          // attributes
   buf[pos + 5] = 0;                          // loop tune
   buf[pos + 6] = 1;                          // channels (mono)
   buf[pos + 7] = 16;                         // bit depth
   writeU32BE(buf, pos + 8, pcm.length);     // num samples
   pos += SMD1_HEADER;
   buf.set(pcmBytes, pos);

   return buf;
}

/**
 * Build a KMP (Korg Multi Program) file referencing KSF files.
 *
 * @param {Object} multisample - { name, samples: [{ ksfName, originalKey, topKey, tune, level, pan }] }
 * @returns {Uint8Array} - KMP file bytes
 */
export function buildKMP(multisample) {
   const name = (multisample.name || 'MULTISMP').substring(0, 16);
   const samples = multisample.samples || [];
   const numSamples = samples.length;
   const msNumber = multisample.number || 0;

   // Chunk sizes
   const MSP1_SIZE = 18;
   const MNO1_SIZE = 4;
   const RLP1_SIZE = numSamples * 18;
   const RLP2_SIZE = 4;

   const msp1Total = 8 + MSP1_SIZE;
   const mno1Total = 8 + MNO1_SIZE;
   const rlp1Total = 8 + RLP1_SIZE + (RLP1_SIZE & 1);
   const rlp2Total = 8 + RLP2_SIZE;

   const totalSize = msp1Total + mno1Total + rlp1Total + rlp2Total;
   const buf = new Uint8Array(totalSize);
   let pos = 0;

   // MSP1 chunk
   writeChunkId(buf, pos, 'MSP1');
   writeU32BE(buf, pos + 4, MSP1_SIZE);
   pos += 8;
   writeAscii(buf, pos, name, 16);
   buf[pos + 16] = numSamples & 0xFF;
   buf[pos + 17] = 0; // attributes
   pos += MSP1_SIZE;

   // MNO1 chunk
   writeChunkId(buf, pos, 'MNO1');
   writeU32BE(buf, pos + 4, MNO1_SIZE);
   pos += 8;
   writeU32BE(buf, pos, msNumber);
   pos += MNO1_SIZE;

   // RLP1 chunk: sample references (18 bytes each)
   writeChunkId(buf, pos, 'RLP1');
   writeU32BE(buf, pos + 4, RLP1_SIZE);
   pos += 8;
   for (let i = 0; i < numSamples; i++) {
      const s = samples[i];
      const off = pos + i * 18;
      buf[off] = (s.originalKey || 60) & 0x7F;   // original key (bit 7 = non-transpose)
      buf[off + 1] = (s.topKey !== undefined ? s.topKey : 127) & 0x7F; // top key
      buf[off + 2] = (s.tune || 0) & 0xFF;        // tune (-99..+99 as signed byte)
      buf[off + 3] = (s.level || 0) & 0xFF;        // level
      buf[off + 4] = (s.pan !== undefined ? s.pan : 64) & 0x7F; // pan (center=64)
      buf[off + 5] = 0;                            // filter cutoff
      writeAscii(buf, off + 6, (s.ksfName || `sample${i}`).substring(0, 12), 12);
   }
   pos += RLP1_SIZE + (RLP1_SIZE & 1);

   // RLP2 chunk
   writeChunkId(buf, pos, 'RLP2');
   writeU32BE(buf, pos + 4, RLP2_SIZE);
   pos += 8;
   buf[pos] = 0;     // transpose
   buf[pos + 1] = 0; // resonance
   buf[pos + 2] = 0; // attack
   buf[pos + 3] = 0; // decay

   return buf;
}

/**
 * Build a KSC (Korg Script) file that lists all KSF/KMP files for loading.
 * This is a simple text file the Triton/M3 reads to batch-load samples.
 *
 * @param {Array} ksfNames - array of KSF filenames (without extension)
 * @param {Array} kmpNames - array of KMP filenames (without extension)
 * @returns {string} - KSC text content
 */
export function buildKSC(ksfNames, kmpNames) {
   let text = '// KORG Sample/Program Load Script\n';
   text += '// Generated by AZOPSOFT XTRC Export\n\n';

   if (kmpNames.length > 0) {
      text += '[MULTISAMPLE]\n';
      for (const name of kmpNames) {
         text += `"${name}.KMP"\n`;
      }
      text += '\n';
   }

   if (ksfNames.length > 0) {
      text += '[SAMPLE]\n';
      for (const name of ksfNames) {
         text += `"${name}.KSF"\n`;
      }
   }

   return text;
}

/**
 * Calculate total size of KSF files for a set of samples.
 * Used for capacity estimation.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {number} - total bytes
 */
export function calcKsfCapacity(samples) {
   let total = 0;
   for (const smp of samples) {
      const pcm = smp.pcm;
      if (!pcm || pcm.length === 0) continue;
      // SMP1(8+32) + SNO1(8+4) + SMD1(8+12+pcm*2)
      const smd1Data = 12 + pcm.length * 2;
      const smd1Padded = smd1Data + (smd1Data & 1);
      total += 40 + 12 + 8 + smd1Padded;
   }
   return total;
}
